import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import {
  appApi,
  useGetNetplusReceiptsQuery,
  useGetNetplusSummaryQuery,
} from "../../../store/appApi";
import { selectUserRole } from "../../../store/authSlice";
import {
  TENURE_OPTIONS,
  TYPE_OPTIONS,
  formatCurrency,
  formatDateLabel,
  formatTenureLabel,
} from "./netplusConfig";
import TransferToEmployeeModal from "./TransferToEmployeeModal";
import { updateNetplusReceiptApi } from "../../../services/netplus";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const typeChip = (type) =>
  type === "Recharge"
    ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200"
    : "bg-violet-50 text-violet-700 ring-1 ring-violet-200";

const NetplusList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [filters, setFilters] = useState({ type: "", date: null });
  const [page, setPage] = useState(1);
  const [showTransferToEmployee, setShowTransferToEmployee] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    isError: summaryError,
    error: summaryErrorData,
  } = useGetNetplusSummaryQuery();
  const { data, isLoading, isFetching } = useGetNetplusReceiptsQuery({
    page,
    limit: 20,
    type: filters.type,
    dateFrom: filters.date ? filters.date.toISOString() : "",
  });

  const receipts = Array.isArray(data?.receipts) ? data.receipts : [];
  const total = Number(data?.total) || 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));
  const summary = summaryData?.summary || {};
  const loading = isLoading || isFetching;
  const summaryStillLoading = summaryLoading || summaryFetching;
  const currentBalance = Number(summary.currentBalance) || 0;

  // Explain *why* the transfer buttons are disabled instead of leaving them
  // looking broken with no feedback.
  const transferDisabledReason = summaryStillLoading
    ? "Loading Netplus balance…"
    : summaryError
      ? summaryErrorData?.data?.message ||
        "Could not load the Netplus balance. Select an active project and try again."
      : currentBalance <= 0
        ? "No balance available to transfer — add a receipt first."
        : "";

  // Amount/date/balance are chained across receipts and never editable here —
  // only descriptive fields can be corrected post-hoc.
  const startEdit = (receipt) => {
    setEditingId(receipt._id);
    setEditDraft({
      type: receipt.type,
      customerId: receipt.customerId || "",
      customerName: receipt.customerName || "",
      receiptNumber: receipt.receiptNumber || "",
      tenure: receipt.tenure,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(null);
  };

  const updateDraft = (key, value) =>
    setEditDraft((prev) => ({ ...prev, [key]: value }));

  const saveEdit = async (receiptId) => {
    if (!editDraft.customerId || !editDraft.customerName || !editDraft.receiptNumber) {
      Swal.fire({
        icon: "warning",
        title: "Missing details",
        text: "Customer ID, customer name, and receipt number are required.",
      });
      return;
    }

    try {
      setRowBusy(receiptId);
      await updateNetplusReceiptApi(receiptId, {
        type: editDraft.type,
        customerId: editDraft.customerId,
        customerName: editDraft.customerName,
        receiptNumber: editDraft.receiptNumber,
        tenure: Number(editDraft.tenure),
      });
      dispatch(appApi.util.invalidateTags(["NetplusReceipts"]));
      Swal.fire({
        icon: "success",
        title: "Updated",
        timer: 1400,
        showConfirmButton: false,
      });
      cancelEdit();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to update",
        text:
          error?.response?.data?.message ||
          "Something went wrong while updating this receipt.",
      });
    } finally {
      setRowBusy(null);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Netplus Receipts" motherMenu="Finance" />

      <div className="space-y-6">
        <section className={`${panelClass} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Netplus Receipts
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
                Receipts Ledger
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-500">
                Every recharge and new-connection payment recorded for this
                project.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                  onClick={() => navigate("/netplus/add")}
                >
                  + Add Receipt
                </button>
                {isSuperAdmin && (
                  <button
                    type="button"
                    className="rounded-full border-2 border-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:border-gray-300 disabled:hover:text-gray-700"
                    onClick={() => setShowTransferToEmployee(true)}
                    disabled={!!transferDisabledReason}
                    title={transferDisabledReason || undefined}
                  >
                    Transfer to Employee
                  </button>
                )}
                <button
                  type="button"
                  className="rounded-full border-2 border-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700"
                  onClick={() => navigate("/netplus/transfers")}
                >
                  Transfer History
                </button>
              </div>
              {isSuperAdmin && transferDisabledReason && (
                <p className="mt-2 text-xs font-medium text-amber-700">
                  {transferDisabledReason}
                </p>
              )}
            </div>

            <div className="grid min-w-[320px] grid-cols-3 gap-3 text-center text-xs text-gray-600">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Total Receipts</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(summary.totalReceipts)}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Total Transferred</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(summary.totalTransferred)}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Current Balance</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(summary.currentBalance)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={`${panelClass} p-5`}>
          <div className="grid gap-4 md:grid-cols-[1fr_1fr_auto]">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Type
              </label>
              <select
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                autoComplete="off"
                value={filters.type}
                onChange={(event) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, type: event.target.value }));
                }}
              >
                <option value="">All types</option>
                {TYPE_OPTIONS.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">
                Date
              </label>
              <DatePicker
                selected={filters.date}
                onChange={(date) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, date }));
                }}
                placeholderText="Filter by date"
                className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                autoComplete="off"
                dateFormat="dd/MM/yyyy"
                isClearable
              />
            </div>

            <div className="flex items-end">
              <button
                type="button"
                className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                onClick={() => {
                  setPage(1);
                  setFilters({ type: "", date: null });
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </section>

        <section className={panelClass}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <h2 className="text-lg font-semibold text-gray-900">All Receipts</h2>
            <div className="text-sm text-gray-500">
              Page <span className="text-gray-900">{page}</span> of{" "}
              <span className="text-gray-900">{totalPages}</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">Date</th>
                  <th className="px-5 py-2.5 text-left font-medium">Type</th>
                  <th className="px-5 py-2.5 text-left font-medium">Customer ID</th>
                  <th className="px-5 py-2.5 text-left font-medium">Customer Name</th>
                  <th className="px-5 py-2.5 text-left font-medium">Receipt No.</th>
                  <th className="px-5 py-2.5 text-right font-medium">Amount</th>
                  <th className="px-5 py-2.5 text-left font-medium">Tenure</th>
                  <th className="px-5 py-2.5 text-right font-medium">Balance</th>
                  <th className="px-5 py-2.5 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading && (
                  <tr>
                    <td colSpan={9} className="px-5 py-10 text-center text-gray-500">
                      Loading receipts...
                    </td>
                  </tr>
                )}

                {!loading && receipts.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-10 text-center text-gray-500">
                      No receipts recorded for the selected filters.
                    </td>
                  </tr>
                )}

                {!loading &&
                  receipts.map((receipt) => {
                    const isEditing = editingId === receipt._id;
                    const busy = rowBusy === receipt._id;

                    if (isEditing) {
                      return (
                        <tr key={receipt._id} className="bg-amber-50/40">
                          <td className="px-5 py-2.5 text-gray-400">
                            {formatDateLabel(receipt.date)}
                          </td>
                          <td className="px-5 py-2.5">
                            <select
                              className={inputClass}
                              value={editDraft.type}
                              onChange={(event) =>
                                updateDraft("type", event.target.value)
                              }
                            >
                              {TYPE_OPTIONS.map((type) => (
                                <option key={type} value={type}>
                                  {type}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-5 py-2.5">
                            <input
                              className={inputClass}
                              value={editDraft.customerId}
                              onChange={(event) =>
                                updateDraft("customerId", event.target.value)
                              }
                            />
                          </td>
                          <td className="px-5 py-2.5">
                            <input
                              className={inputClass}
                              value={editDraft.customerName}
                              onChange={(event) =>
                                updateDraft("customerName", event.target.value)
                              }
                            />
                          </td>
                          <td className="px-5 py-2.5">
                            <input
                              className={inputClass}
                              value={editDraft.receiptNumber}
                              onChange={(event) =>
                                updateDraft("receiptNumber", event.target.value)
                              }
                            />
                          </td>
                          <td className="px-5 py-2.5 text-right text-gray-400">
                            {formatCurrency(receipt.amount)}
                          </td>
                          <td className="px-5 py-2.5">
                            <select
                              className={inputClass}
                              value={editDraft.tenure}
                              onChange={(event) =>
                                updateDraft("tenure", event.target.value)
                              }
                            >
                              {TENURE_OPTIONS.map((months) => (
                                <option key={months} value={months}>
                                  {formatTenureLabel(months)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-5 py-2.5 text-right text-gray-400">
                            {formatCurrency(receipt.balance)}
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                disabled={busy}
                                className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                                onClick={() => saveEdit(receipt._id)}
                              >
                                {busy ? "Saving..." : "Save"}
                              </button>
                              <button
                                type="button"
                                disabled={busy}
                                className="rounded-lg border-2 border-amber-500 px-3 py-1.5 text-xs font-semibold text-gray-600"
                                onClick={cancelEdit}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={receipt._id}>
                        <td className="px-5 py-2.5 text-gray-600">
                          {formatDateLabel(receipt.date)}
                        </td>
                        <td className="px-5 py-2.5">
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${typeChip(receipt.type)}`}
                          >
                            {receipt.type}
                          </span>
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {receipt.customerId}
                        </td>
                        <td className="px-5 py-2.5 font-semibold text-gray-900">
                          {receipt.customerName}
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {receipt.receiptNumber}
                        </td>
                        <td className="px-5 py-2.5 text-right text-gray-900">
                          {formatCurrency(receipt.amount)}
                        </td>
                        <td className="px-5 py-2.5 text-gray-600">
                          {formatTenureLabel(receipt.tenure)}
                        </td>
                        <td className="px-5 py-2.5 text-right font-semibold text-gray-900">
                          {formatCurrency(receipt.balance)}
                        </td>
                        <td className="px-5 py-2.5 text-right">
                          <button
                            type="button"
                            disabled={busy}
                            className="rounded-lg border-2 border-amber-500 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={() => startEdit(receipt)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 text-sm text-gray-500">
            <div>
              Showing {receipts.length} of {total} receipt{total === 1 ? "" : "s"}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-700 disabled:opacity-50"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              >
                Prev
              </button>
              <button
                type="button"
                className="rounded-xl border-2 border-amber-500 px-3 py-2 text-xs font-semibold text-gray-700 disabled:opacity-50"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </section>
      </div>

      {showTransferToEmployee && (
        <TransferToEmployeeModal
          currentBalance={currentBalance}
          onClose={() => setShowTransferToEmployee(false)}
        />
      )}
    </>
  );
};

export default NetplusList;
